import type { DashboardOverview } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { formatNumber } from "@/utils/tools";
import { ErrorBoundary } from "@st-marketplace/ui";
import type { FC, ReactNode } from "react";
import {
	AddOrderButton,
	AddOrderDialogHost,
	DashboardRecentOrders,
	DashboardSection,
	DashboardSendersHealth,
	DashboardTopBuyers,
	StatTile,
} from "./components";

interface DashboardSellViewProps {
	loading: boolean;
	error: Error | null;
	data: DashboardOverview;
	refetch: () => void;
	filter: ReactNode;
}

/** Seller home: performance for the window, fleet health, then who and what. */
const DashboardSellView: FC<DashboardSellViewProps> = ({
	loading,
	error,
	data,
	refetch,
	filter,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-8">
			<DashboardSection
				title={t("home.salesPerformance")}
				icon="line-chart-line"
				trailing={filter}
			>
				<ErrorBoundary
					isLoading={false}
					error={error ?? undefined}
					onReset={refetch}
				>
					<div className="dashboard-stats-grid">
						<StatTile
							label={t("home.totalDelivered")}
							value={formatNumber(data.total_delivered, "large_count")}
							icon="truck-line"
							loading={loading}
						/>
						<StatTile
							label={t("home.snipeSuccessRatio")}
							value={formatNumber(data.snipe_success_ratio, "percent")}
							icon="checkbox-circle-line"
							loading={loading}
						/>
						<StatTile
							label={t("home.globalTax")}
							value={formatNumber(data.global_tax, "percent")}
							icon="percent-line"
							loading={loading}
						/>
					</div>
				</ErrorBoundary>
			</DashboardSection>

			<div className="dashboard-seller-layout">
				<div className="dashboard-seller-stack">
					<DashboardSendersHealth data={data} loading={loading} />
					<DashboardTopBuyers
						debtsToReceive={Number(data.debts_to_receive)}
						loading={loading}
					/>
				</div>
				<DashboardRecentOrders
					action={<AddOrderButton side="sell" />}
					side="seller"
					title={t("home.recentSales")}
					limit={10}
				/>
			</div>
			<AddOrderDialogHost />
		</div>
	);
};

export default DashboardSellView;
